import { NavLink } from 'react-router';
import type { UserRole } from '@seenaly/shared';
import { usePrefs } from '@/app/prefs';
import { visibleGroups } from '@/app/nav';
import { Icon, Tag } from '@/components/ui';

export interface SidebarBadges {
  appointmentsToday?: number;
  lowStock?: number;
}

interface SidebarProps {
  role: UserRole;
  visibleNavKeys: readonly string[];
  open: boolean;
  badges?: SidebarBadges;
  /** Closes the drawer after a tap on mobile. */
  onNavigate: () => void;
}

export function Sidebar({ role, visibleNavKeys, open, badges, onNavigate }: SidebarProps) {
  const { t } = usePrefs();

  return (
    <nav className="sn-sidebar" data-open={open} aria-label={t.menuMain}>
      <div className="sn-brand">
        <div className="sn-brand-mark">
          {/* Served from public/, so it is referenced by URL rather than imported. */}
          <img src="/logo.png" alt="" />
        </div>
        <div>
          <div className="sn-brand-name">{t.clinicName}</div>
          <div className="sn-brand-kicker">{t.clinicKicker}</div>
        </div>
      </div>

      {visibleGroups(role, visibleNavKeys).map((group) => (
        <div key={group.titleKey}>
          <div className="sn-group">{t[group.titleKey]}</div>
          <div className="sn-navlist">
            {group.items.map((item) => {
              const badge = item.badge ? badges?.[item.badge] : undefined;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  // `end` only on "/", or the dashboard would stay highlighted
                  // on every other route.
                  end={item.to === '/'}
                  className="sn-navitem"
                  onClick={onNavigate}
                >
                  <Icon name={item.icon} />
                  <span className="sn-navitem-label">{t.nav[item.labelKey]}</span>
                  {badge !== undefined && badge > 0 && <Tag tone="accent">{badge}</Tag>}
                </NavLink>
              );
            })}
          </div>
        </div>
      ))}

      <div className="sn-sidebar-foot">
        <div className="card sn-helpcard">
          <div className="sn-helpcard-title">{t.helpTitle}</div>
          <div className="sn-helpcard-body">{t.helpBody}</div>
        </div>
      </div>
    </nav>
  );
}
